/**
 * Trunnion AI - Audit Log Query Hooks
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { queryKeys } from '@/config/queryKeys';
import { auditApi } from '@/api/audit.api';
import { useNotificationStore } from '@/stores/notificationStore';
import type { AuditLogFilters, ATOExportConfig } from '@/types';

export function useAuditLogList(filters: AuditLogFilters = {}) {
  return useQuery({
    queryKey: queryKeys.audit.list(filters as Record<string, unknown>),
    queryFn: () => auditApi.list(filters),
  });
}

export function useAuditExport() {
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: (config: ATOExportConfig) => auditApi.export(config),
    onSuccess: (blob, variables) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = variables.format === 'csv' ? 'csv' : 'json';
      a.download = `trunnion-ai-audit-export.${ext}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      addNotification({
        type: 'success',
        title: 'Export Complete',
        message: 'Audit log has been exported successfully.',
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Export Failed',
        message: error.message,
      });
    },
  });
}

export function useAuditStats(params?: { startDate?: string; endDate?: string }) {
  return useQuery({
    queryKey: [...queryKeys.audit.all, 'stats', params],
    queryFn: () => auditApi.getStats(params),
  });
}
